import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { TherapistPhotoCard } from './TherapistPhotoCard';

interface OpenClinician {
  id: string;
  displayName: string;
  specializations: string[];
  hourlyRateNgn: number;
  profilePhotoUrl?: string | null;
  nextSlot: string;
  ratingAvg?: number;
  ratingCount?: number;
}

function cardStep(track: HTMLDivElement): number {
  const card = track.firstElementChild;
  if (!(card instanceof HTMLElement)) return 272;
  const gap = Number.parseFloat(window.getComputedStyle(track).columnGap);
  return card.offsetWidth + (Number.isFinite(gap) ? gap : 16);
}

export function OpenTodaySection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const [clinicians, setClinicians] = useState<OpenClinician[]>([]);
  const [failed, setFailed] = useState(false);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  useEffect(() => {
    api
      .get<OpenClinician[]>('/therapists/open-today')
      .then((rows) => {
        setClinicians(rows);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      setCanScrollBack(track.scrollLeft > 8);
      setCanScrollForward(track.scrollLeft + track.clientWidth < track.scrollWidth - 8);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    track.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer.disconnect();
      track.removeEventListener('scroll', measure);
    };
  }, [clinicians.length]);

  useEffect(() => {
    if (clinicians.length < 2) return;
    const id = window.setInterval(() => {
      const track = trackRef.current;
      if (!track || pausedRef.current) return;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 8;
      if (atEnd) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      track.scrollBy({ left: cardStep(track), behavior: 'smooth' });
    }, 3500);
    return () => window.clearInterval(id);
  }, [clinicians.length]);

  const scrollTrack = (direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * cardStep(track), behavior: 'smooth' });
  };

  if (failed || clinicians.length === 0) return null;

  return (
    <section className="landing-section min-w-0">
      <div className="mx-auto w-full min-w-0 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="font-serif text-3xl font-medium text-[#1C1917]">Available today</h2>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              aria-label="Previous clinicians"
              disabled={!canScrollBack}
              onClick={() => scrollTrack(-1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-stone-300 bg-white text-lg text-stone-700 disabled:cursor-default disabled:opacity-30"
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Next clinicians"
              disabled={!canScrollForward}
              onClick={() => scrollTrack(1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-stone-300 bg-white text-lg text-stone-700 disabled:cursor-default disabled:opacity-30"
            >
              ›
            </button>
            <Link to="/therapists" className="text-midnight ml-2 text-sm font-semibold">
              View all →
            </Link>
          </div>
        </div>
        <div
          ref={trackRef}
          className="mt-8 flex w-full min-w-0 snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-smooth pb-2"
          onPointerDown={() => {
            pausedRef.current = true;
          }}
          onPointerUp={() => {
            window.setTimeout(() => {
              pausedRef.current = false;
            }, 3500);
          }}
          onPointerCancel={() => {
            pausedRef.current = false;
          }}
        >
          {clinicians.map((clinician) => (
            <TherapistPhotoCard
              key={clinician.id}
              id={clinician.id}
              displayName={clinician.displayName}
              specializations={clinician.specializations}
              profilePhotoUrl={clinician.profilePhotoUrl}
              hourlyRateNgn={clinician.hourlyRateNgn}
              ratingAvg={clinician.ratingAvg}
              ratingCount={clinician.ratingCount}
              nextSlot={clinician.nextSlot}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
